import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiResponse, ServiceBase } from './base/service.base';
import { Topic } from '../utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { ErrorRecord } from '@app/origami-api/utils/error-utils';

export namespace CountryWorkflowManagementServiceModel {
  export enum CountryMilestoneTemplateStatusType {
    Active = 0,
    Inactive = 1,
  }

  export enum CountryWorkflowStatusType {
    Draft = 1,
    Published = 2,
    Retired = 3,
  }

  export enum CountryWorkflowMilestoneStatusType {
    Planned = 1,
    Completed = 2,
    NotApplicable = 3,
  }

  export interface NavigationParams {
    Start?: number;
    Limit?: number;
    TotalCount?: number;
    EntityId?: number;
    PageNumber?: number;
    OrderBy?: string;
  }

  export interface GetResultBase<T> {
    TotalRowCount: number;
    PageNumber: number;
    Records: T[];
  }

  export interface CountryWorkflow extends Topic {
    WorkflowMilestones: Topic[];
    EntityMilestones: Topic[];
  }

  export interface CountryWorkflowTemplate extends Topic {
    MilestoneTemplates: Topic[];
  }

  export interface CountryWorkflowFilterOptions {
    CountryTopicId?: number | null;
    CountryIso2?: string;
    InvestigativeSiteId?: number | null;
    ReturnWorkflowMilestones?: boolean;
    ReturnCountryMilestones?: boolean;
    ReturnSiteMilestones?: boolean;
  }

  export interface GetCountryWorkflowRequest {
    siteId: number;
    filterOptions?: CountryWorkflowFilterOptions;
  }

  export interface CountryWorkflowTemplatesFilterOptions {
    SearchQuery?: string;
    ClientId?: number | null;
    InvestigativeSiteId?: number | null;
    CountryIso2?: string;
    FieldListing?: string;
  }

  export interface GetCountryWorkflowTemplatesRequest {
    siteId: number;
    filterOptions?: CountryWorkflowTemplatesFilterOptions;
    navigationParams?: NavigationParams;
  }

  export interface SaveCountryWorkflowsRequest {
    siteId: number;
    workflows: CountryWorkflow[];
  }

  export interface SaveCountryWorkflowTemplatesRequest {
    siteId: number;
    templates: CountryWorkflowTemplate[];
  }

  export interface DeleteCountryWorkflowTemplatesRequest {
    siteId: number;
    workflowTemplateTopicIds: number[];
  }

  export interface ChangeCountryMilestoneTemplateStatusRequest {
    siteId: number;
    topicId: number;
    status: CountryMilestoneTemplateStatusType;
    comments: string;
  }

  export interface SaveCountryMilestoneTemplatesRequest {
    siteId: number;
    templates: Topic[];
  }

  export interface ChangeCountryWorkflowTemplateStatusRequest {
    siteId: number;
    topicId: number;
    status: CountryWorkflowStatusType;
    comments: string;
  }

  export interface ChangeCountryWorkflowStatusRequest {
    siteId: number;
    topicId: number;
    status: CountryWorkflowStatusType;
    comments: string;
  }

  export interface ChangeCountryWorkflowMilestoneStatusRequest {
    siteId: number;
    entityTopicId?: number; // deprecated
    milestoneTopicId: number;
    status: CountryWorkflowMilestoneStatusType;
    comments: string;
    actualDate?: string | Date | null;
  }

  export interface SaveCountryMilestoneTemplatesInWorkflowRequest {
    siteId: number;
    parentTopicId: number;
    milestoneTemplates: Topic[];
  }

  export interface CreateCountryWorkflowRequest {
    siteId: number;
    workflow: CountryWorkflow;
  }

  export interface UpdateCountryWorkflowRequest {
    siteId: number;
    workflow: CountryWorkflow;
  }

  export interface CreateCountryWorkflowMilestoneRequest {
    siteId: number;
    milestone: Topic;
  }

  export interface UpdateCountryWorkflowMilestoneRequest {
    siteId: number;
    milestone: Topic;
  }

  export interface DeleteCountryMilestoneTemplatesInWorkflowRequest {
    siteId: number;
    topicIds: number[];
  }

  export interface CountryMilestoneFilterOptions {
    SearchQuery?: string;
    FieldListing?: string;
    ReturnStatistics?: boolean;
  }

  export interface GetCountryMilestoneInfoRequest {
    siteId: number;
    filterOptions?: CountryMilestoneFilterOptions;
    navigationParams?: NavigationParams;
  }

  export interface CountryMilestoneInfo {
    CompletedPercentage: number;
    CountriesNumber: number;
    SitesNumber: number;
    MilestonesNumber: number;
    PlannedNumber: number;
    CompletedNumber: number;
    OverdueNumber: number;
    TotalRowCount: number;
    PageNumber?: number | null;
    Milestones: Topic[];
  }

  export interface DeleteCountryWorkflowMilestonesRequest {
    siteId: number;
    topicIds: number[];
  }

  export interface SaveCountryWorkflowTemplatesResponse {
    ErrorCode: number;
    Result: ErrorRecord[];
  }
}

@Injectable({ providedIn: 'root' })
export class CountryWorkflowManagementService extends ServiceBase {
  serviceUrl = 'CountryWorkflowManagementService.svc';

  getCountryWorkflow(
    body: CountryWorkflowManagementServiceModel.GetCountryWorkflowRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<CountryWorkflowManagementServiceModel.CountryWorkflow>> {
    return this.origamiPost('/GetCountryWorkflow', body, options, silentErrorCodes, forceRequest);
  }

  getCountryWorkflowTemplates(
    body: CountryWorkflowManagementServiceModel.GetCountryWorkflowTemplatesRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<
    OrigamiResponse<
      CountryWorkflowManagementServiceModel.GetResultBase<CountryWorkflowManagementServiceModel.CountryWorkflowTemplate>
    >
  > {
    return this.origamiPost('/GetCountryWorkflowTemplates', body, options, silentErrorCodes, forceRequest);
  }

  saveCountryWorkflows(
    body: CountryWorkflowManagementServiceModel.SaveCountryWorkflowsRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/SaveCountryWorkflows', body, options, silentErrorCodes, forceRequest);
  }

  saveCountryWorkflowTemplates(
    body: CountryWorkflowManagementServiceModel.SaveCountryWorkflowTemplatesRequest,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/SaveCountryWorkflowTemplates', body, null, silentErrorCodes, true);
  }

  deleteCountryWorkflowTemplates(
    body: CountryWorkflowManagementServiceModel.DeleteCountryWorkflowTemplatesRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/DeleteCountryWorkflowTemplates', body, options, silentErrorCodes, forceRequest);
  }

  changeCountryMilestoneTemplateStatus(
    body: CountryWorkflowManagementServiceModel.ChangeCountryMilestoneTemplateStatusRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/ChangeCountryMilestoneTemplateStatus', body, options, silentErrorCodes, forceRequest);
  }

  saveCountryMilestoneTemplates(
    body: CountryWorkflowManagementServiceModel.SaveCountryMilestoneTemplatesRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/SaveCountryMilestoneTemplates', body, options, silentErrorCodes, forceRequest);
  }

  changeCountryWorkflowTemplateStatus(
    body: CountryWorkflowManagementServiceModel.ChangeCountryWorkflowTemplateStatusRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/ChangeCountryWorkflowTemplateStatus', body, options, silentErrorCodes, forceRequest);
  }

  changeCountryWorkflowStatus(
    body: CountryWorkflowManagementServiceModel.ChangeCountryWorkflowStatusRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/ChangeCountryWorkflowStatus', body, options, silentErrorCodes, forceRequest);
  }

  changeCountryWorkflowMilestoneStatus(
    body: CountryWorkflowManagementServiceModel.ChangeCountryWorkflowMilestoneStatusRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/ChangeCountryWorkflowMilestoneStatus', body, options, silentErrorCodes, forceRequest);
  }

  saveCountryMilestoneTemplatesInWorkflow(
    body: CountryWorkflowManagementServiceModel.SaveCountryMilestoneTemplatesInWorkflowRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/SaveCountryMilestoneTemplatesInWorkflow', body, options, silentErrorCodes, forceRequest);
  }

  createCountryWorkflow(
    body: CountryWorkflowManagementServiceModel.CreateCountryWorkflowRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/CreateCountryWorkflow', body, options, silentErrorCodes, forceRequest);
  }

  updateCountryWorkflow(
    body: CountryWorkflowManagementServiceModel.UpdateCountryWorkflowRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/UpdateCountryWorkflow', body, options, silentErrorCodes, forceRequest);
  }

  createCountryWorkflowMilestone(
    body: CountryWorkflowManagementServiceModel.CreateCountryWorkflowMilestoneRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/CreateCountryWorkflowMilestone', body, options, silentErrorCodes, forceRequest);
  }

  updateCountryWorkflowMilestone(
    body: CountryWorkflowManagementServiceModel.UpdateCountryWorkflowMilestoneRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/UpdateCountryWorkflowMilestone', body, options, silentErrorCodes, forceRequest);
  }

  deleteCountryMilestoneTemplatesInWorkflow(
    body: CountryWorkflowManagementServiceModel.DeleteCountryMilestoneTemplatesInWorkflowRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost(
      '/DeleteCountryMilestoneTemplatesInWorkflow',
      body,
      options,
      silentErrorCodes,
      forceRequest,
    );
  }

  getCountryMilestoneInfo(
    body: CountryWorkflowManagementServiceModel.GetCountryMilestoneInfoRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<CountryWorkflowManagementServiceModel.CountryMilestoneInfo>> {
    return this.origamiPost('/GetCountryMilestoneInfo', body, options, silentErrorCodes, forceRequest);
  }

  // @deprecated Deleted on backend: CountryWorkflowManagementService.svc has no /GetCountryWorkflows endpoint.
  getCountryWorkflows(
    body: any,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<any> {
    return this.origamiPost('/GetCountryWorkflows', body, options, silentErrorCodes, forceRequest);
  }

  // @deprecated Deleted on backend: CountryWorkflowManagementService.svc has no /DeleteCountryWorkflowMilestones endpoint.
  deleteCountryWorkflowMilestones(
    body: CountryWorkflowManagementServiceModel.DeleteCountryWorkflowMilestonesRequest,
    options?: any,
    silentErrorCodes?: ErrorCode[],
    forceRequest = true,
  ): Observable<OrigamiResponse<ErrorRecord[]>> {
    return this.origamiPost('/DeleteCountryWorkflowMilestones', body, options, silentErrorCodes, forceRequest);
  }
}
